import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createPaypalAccountLink } from '../services/paypalService';

const STORE_API_URL = process.env.REACT_APP_API_URL;

function PaypalSettings() {
  const { token } = useContext(AuthContext);
  const [settings, setSettings] = useState({
    paypalAccountId: '',
    paypalAccountCreatedAt: '',
    paypalOnboardingComplete: false,
    paypalOnboardingCompletedAt: '',
    paypalEmail: '',
  });
  const [paypalOnboardLoading, setPaypalOnboardLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Add state for manual update form
  const [manualUpdateForm, setManualUpdateForm] = useState({
    accountId: '',
    email: ''
  });
  const [showManualUpdateForm, setShowManualUpdateForm] = useState(false);

  useEffect(() => {
    if (token) {
      fetchPaypalSettings();
    }
  }, [token]);

  const fetchPaypalSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${STORE_API_URL}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) throw new Error('Failed to fetch PayPal settings');
      
      const data = await response.json();
      const paypalAccount = data.paypal_accounts?.[0] || {};
      
      setSettings({
        paypalAccountId: paypalAccount.account_id || '',
        paypalAccountCreatedAt: paypalAccount.created_at || '',
        paypalOnboardingComplete: paypalAccount.onboarding_complete || false,
        paypalOnboardingCompletedAt: paypalAccount.onboarding_completed_at || '',
        paypalEmail: paypalAccount.merchant_email || paypalAccount.email || '',
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaypalAccountOnboard = async () => {
    setPaypalOnboardLoading(true);
    setError(null);

    try {
      // Create PayPal onboarding link
      const accountLink = await createPaypalAccountLink(token);
      console.log('PayPal account link created:', accountLink);

      // Redirect to PayPal onboarding link
      window.location.href = accountLink.url;
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setPaypalOnboardLoading(false);
    }
  };

  // New function to refresh PayPal status
  const handleRefreshStatus = async () => {
    setRefreshLoading(true);
    try {
      const response = await fetch(`${STORE_API_URL}/paypal/refresh-merchant-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to refresh PayPal status');
      }

      const data = await response.json();
      toast.success('PayPal account status refreshed successfully');
      
      // Refresh settings to show updated info
      fetchPaypalSettings();
    } catch (error) {
      toast.error(error.message || 'Failed to refresh PayPal status');
    } finally {
      setRefreshLoading(false);
    }
  };

  // Add handler for manual update form
  const handleManualUpdateChange = (e) => {
    const { name, value } = e.target;
    setManualUpdateForm({
      ...manualUpdateForm,
      [name]: value
    });
  };

  // Add function to submit manual update
  const handleManualUpdate = async (e) => {
    e.preventDefault();
    setRefreshLoading(true);
    try {
      const response = await fetch(`${STORE_API_URL}/paypal/update-account-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          accountId: manualUpdateForm.accountId,
          email: manualUpdateForm.email
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update merchant details');
      }

      const data = await response.json();
      toast.success('PayPal merchant details updated successfully');
      
      // Refresh settings to show updated info
      fetchPaypalSettings();
      setShowManualUpdateForm(false);
    } catch (error) {
      toast.error(error.message || 'Failed to update merchant details');
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleNavigateToSettings = () => {
    navigate('/settings');
  };

  return (
    <div className="container">
      <ToastContainer />
      <div className="headline d-flex justify-content-between align-items-center">
        <h2 className="underline">PayPal Settings</h2>
        {!settings.paypalOnboardingComplete && (
          <div className="form-group">
            <button className="btn btn-primary" onClick={handlePaypalAccountOnboard} disabled={paypalOnboardLoading}>
              {paypalOnboardLoading ? 'Loading...' : 'Start Onboarding'}
            </button>
          </div>
        )}
      </div>
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <button className="btn btn-primary me-2 mb-2" onClick={handleNavigateToSettings}>Back to Settings</button>
        </div>
      </div>
      {!loading && !error && !settings.paypalOnboardingComplete && (
        <div className="col-md-12 mt-3">
          <div className="card mb-3 p-3" style={{ border: '2px solid #0070ba' }}>
            <div className="card-body">
              <h5 className="card-title" style={{ color: '#0070ba', fontWeight: 'bold' }}>Setup PayPal to start accepting online payments.</h5>
              <p>Onboarding is handled by PayPal. Your personal information stays with PayPal. PayPal will connect your PayPal Business account to Sunthar.com so that your users can pay for your products and services. Your personal information is never shared by PayPal. You will be directly responsible for chargebacks and disputes. If you have any questions about this, please don't hesitate to reach out.</p>
              <button className="btn btn-primary" onClick={handlePaypalAccountOnboard} disabled={paypalOnboardLoading}>
                {paypalOnboardLoading ? 'Loading...' : 'Start Onboarding'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div>
        {settings.paypalAccountId ? (
          // Show account info when available
          <div>
            <div className="form-group">
              <label>PayPal Account ID</label>
              <input
                type="text"
                className="form-control"
                value={settings.paypalAccountId ? settings.paypalAccountId : 'Not available'}
                readOnly
                disabled
              />
            </div>
            <div className="form-group">
              <label>PayPal Email</label>
              <input
                type="text"
                className="form-control"
                value={settings.paypalEmail ? settings.paypalEmail : 'Not available'}
                readOnly
                disabled
              />
            </div>
            <div className="form-group">
              <label>Onboarding Status</label>
              <input
                type="text"
                className="form-control"
                value={settings.paypalOnboardingComplete ? 'Completed' : 'Not started'}
                readOnly
                disabled
              />
            </div>
            <div className="form-group">
              <label>Onboarded Date</label>
              <input
                type="text"
                className="form-control"
                value={settings.paypalOnboardingCompletedAt ? new Date(settings.paypalOnboardingCompletedAt).toLocaleString() : 'Not complete'}
                readOnly
                disabled
              />
            </div>
          </div>
        ) : (
          // Show a message when no account info is available
          <div className="alert alert-warning">
            <p>No PayPal account information found. Please start onboarding or manually add your details.</p>
          </div>
        )}
        
        {/* Always show these buttons */}
        <div className="form-group mt-3">
          <button 
            className="btn btn-primary" 
            onClick={handlePaypalAccountOnboard} 
            disabled={paypalOnboardLoading}
          >
            {paypalOnboardLoading ? 'Loading...' : 'Start Onboarding'}
          </button>
          <button 
            className="btn btn-secondary ms-2" 
            onClick={() => setShowManualUpdateForm(!showManualUpdateForm)}
          >
            {showManualUpdateForm ? 'Cancel' : 'Manual Update'}
          </button>
          {settings.paypalAccountId && (
            <button 
              className="btn btn-outline-primary ms-2" 
              onClick={handleRefreshStatus} 
              disabled={refreshLoading}
            >
              {refreshLoading ? 'Refreshing...' : 'Refresh Status'}
            </button>
          )}
        </div>
        
        {/* Always allow manual update form to be shown */}
        {showManualUpdateForm && (
          <form onSubmit={handleManualUpdate} className="mt-3 border p-3 rounded">
            <h5>{settings.paypalAccountId ? 'Update PayPal Details' : 'Add PayPal Details'}</h5>
            <div className="form-group">
              <label>PayPal Account ID</label>
              <input
                type="text"
                className="form-control"
                name="accountId"
                value={manualUpdateForm.accountId}
                onChange={handleManualUpdateChange}
                required
              />
            </div>
            <div className="form-group">
              <label>PayPal Email</label>
              <input
                type="email"
                className="form-control"
                name="email"
                value={manualUpdateForm.email}
                onChange={handleManualUpdateChange}
                required
              />
            </div>
            <button type="submit" className="btn btn-primary mt-2" disabled={refreshLoading}>
              {refreshLoading ? 'Updating...' : settings.paypalAccountId ? 'Update Details' : 'Add Account'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default PaypalSettings;
